import React from 'react';
import { Rect, Ellipse, Line, Star, Transformer } from 'react-konva';
import { ShapeElement } from '@/types/ShapeElement';
import { calculateSnapping } from "../utils/snapUtils";
import Konva from "konva"; //  This is fine

interface KonvaShapeItemProps {
    shape: ShapeElement;
    isSelected: boolean;
    onSelect: () => void;
    onChange: (newAttrs: Partial<ShapeElement>) => void;
    referenceRects: { x: number; y: number; width: number; height: number }[];
    stageWidth: number;
    stageHeight: number;
    onUpdateGuides?: (guides: { points: number[] }[]) => void;
}
const KonvaShapeItem: React.FC<KonvaShapeItemProps> = ({
    shape,
    isSelected,
    onSelect,
    onChange,
    referenceRects,
    stageWidth,
    stageHeight,
    onUpdateGuides,
}) => {
    const shapeRef = React.useRef<any>(null);
    const transformerRef = React.useRef<any>(null);
    const [isHovered, setIsHovered] = React.useState(false);
    const [hoverRect, setHoverRect] = React.useState<{ x: number; y: number }[] | null>(null);


    const {
        x, y, rotation = 0, width, height,
        fill = 'transparent', opacity = 1,
       // strokeColor = 'black', strokeWidth = 0,
        shadowColor, shadowBlur = 0, shadowOpacity = 0,
        shadowOffsetX = 0, shadowOffsetY = 0,
        type,
    } = shape;

    // Attach transformer
    React.useEffect(() => {
        if (isSelected && transformerRef.current && shapeRef.current) {
            transformerRef.current.nodes([shapeRef.current]);
            transformerRef.current.getLayer()?.batchDraw();
        }
    }, [isSelected, width, height, rotation]);


    React.useEffect(() => {
        if (isHovered && shapeRef.current) {
            const node = shapeRef.current;
            const rot = Konva.getAngle(node.rotation() || 0);
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);

            let w: number, h: number, offsetX: number, offsetY: number;

            // Center-based shapes
            if (node.className === "Ellipse" || node.className === "Circle" || node.className === "Star") {
                w = node.width();
                h = node.height();
                offsetX = w / 2;
                offsetY = h / 2;
            } else {
                // Rect, Line, Triangle
                w = node.width();
                h = node.height();
                offsetX = 0;
                offsetY = 0;
            }

            const cx = node.x();
            const cy = node.y(); 

            const corners = [
                { x: -offsetX, y: -offsetY },
                { x: w - offsetX, y: -offsetY },
                { x: w - offsetX, y: h - offsetY },
                { x: -offsetX, y: h - offsetY },
            ].map((p) => {
                const x = cx + p.x * cos - p.y * sin;
                let y = cy + p.x * sin + p.y * cos;           
                return { x, y };
            });
           
            setHoverRect(corners);
        } else {
            setHoverRect(null);
        }
    }, [isHovered, shape]);


    const handleDragEnd = (e: any) => {
        onChange({ x: e.target.x(), y: e.target.y() });
        onUpdateGuides?.([]);
    };

    const handleDragMove = (e: any) => {
        const node = e.target;
        const rect = node.getClientRect({ skipShadow: true, skipStroke: true });
        const offsetX = node.x() - rect.x;
        const offsetY = node.y() - rect.y;

        const movingRect = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
        const { x: snappedX, y: snappedY, guides } = calculateSnapping({
            movingRect,
            referenceRects,        // passed from parent
            stageSize: { width: stageWidth, height: stageHeight },
            snapThreshold: 6,
        });

        node.x(snappedX + offsetX);
        node.y(snappedY + offsetY);

        onUpdateGuides?.(guides);
    };


    const handleTransformEnd = () => {
        const node = shapeRef.current;
        if (!node) return;

        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);

        const updated: Partial<ShapeElement> = {
            x: node.x(),
            y: node.y(),
            rotation: node.rotation(),
        };

        if (type === 'ellipse') {
            const newRadiusX = Math.max(5, (width / 2) * scaleX);
            const newRadiusY = Math.max(5, (height / 2) * scaleY);
            onChange({
                ...updated,
                width: newRadiusX * 2,
                height: newRadiusY * 2,
            });
        } else {
            onChange({
                ...updated,
                width: Math.max(10, width * scaleX),
                height: Math.max(10, height * scaleY),
            });
        }
        onUpdateGuides?.([]);
    };

    const commonProps = {
        ref: shapeRef,
        x,
        y,
        rotation,
        fill,
        opacity,
        shadowColor,
        shadowBlur,
        shadowOpacity,
        shadowOffset: { x: shadowOffsetX, y: shadowOffsetY },
        draggable: true,
        onClick: onSelect,
        onTap: onSelect,
        onDragStart: () => onSelect(),
        onDragEnd: handleDragEnd,
        onDragMove: handleDragMove,
        onTransformEnd: handleTransformEnd,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
    };


    const renderShape = () => {
        switch (type) {
            case 'rect':
                return <Rect {...commonProps} width={width} height={height} />;

            case 'ellipse':
                return <Ellipse {...commonProps} radiusX={width / 2} radiusY={height / 2} />;

            case 'triangle': {
                const points = [0, height, width / 2, 0, width, height];
                return <Line {...commonProps} points={points} closed />;
            }

            case 'star': {
                const outerRadius = Math.min(width, height) / 2;
                const innerRadius = outerRadius / 2;
                return <Star {...commonProps} numPoints={5} innerRadius={innerRadius} outerRadius={outerRadius} />;
            }

            default:
                return null;
        }
    };


    return (
        <>
            {renderShape()}

            {/* Hover bounding box */}
            {hoverRect && !isSelected && (        
                <Line
                    points={hoverRect.flatMap((p) => [p.x, p.y])}
                    closed
                    stroke="#59b2d8"
                    strokeWidth={2}
                    listening={false}
                />
            )}

            {/* Transformer */}
            {isSelected && (
                <Transformer
                    ref={transformerRef}
                    rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
                    rotationSnapTolerance={8}
                    borderStroke="#59b2d8"
                    borderStrokeWidth={2}
                    anchorSize={12}
                    anchorStrokeWidth={2}
                    rotateAnchorOffset={35}
                    anchorCornerRadius={1}
                    anchorStyleFunc={(anchor) => {                       
                        // make rotate node circle
                        if (anchor.hasName('rotater')) {
                            anchor.scale({ x: 1.5, y: 1.5 });
                            anchor.cornerRadius(50);
                            anchor.strokeWidth(1.5);
                        }
                       
                    }}
                    anchorDragBoundFunc={(oldPos, newPos) => {
                        const SNAP_DISTANCE = 6;
                        const guides: { points: number[] }[] = [];

                        // Build "otherRects" from referenceRects prop
                        const otherRects = referenceRects.map(r => ({
                            left: r.x,
                            right: r.x + r.width,
                            top: r.y,
                            bottom: r.y + r.height
                        }));

                        let snappedX = newPos.x;
                        let snappedY = newPos.y;
                        let snapped = false;

                        // Snap X
                        for (let rect of otherRects) {
                            if (Math.abs(newPos.x - rect.left) < SNAP_DISTANCE) {
                                snappedX = rect.left;
                                guides.push({ points: [rect.left, 0, rect.left, stageHeight] });
                                snapped = true;
                                break;
                            }
                            if (Math.abs(newPos.x - rect.right) < SNAP_DISTANCE) {
                                snappedX = rect.right;
                                guides.push({ points: [rect.right, 0, rect.right, stageHeight] });
                                snapped = true;
                                break;
                            }
                        }

                        // Snap Y
                        for (let rect of otherRects) {
                            if (Math.abs(newPos.y - rect.top) < SNAP_DISTANCE) {
                                snappedY = rect.top;
                                guides.push({ points: [0, rect.top, stageWidth, rect.top] });
                                snapped = true;
                                break;
                            }
                            if (Math.abs(newPos.y - rect.bottom) < SNAP_DISTANCE) {
                                snappedY = rect.bottom;
                                guides.push({ points: [0, rect.bottom, stageWidth, rect.bottom] });
                                snapped = true;
                                break;
                            }
                        }

                        // Update guides
                        onUpdateGuides?.(snapped ? guides : []);
                        return { ...newPos, x: snappedX, y: snappedY };
                    }}
                />
            )}
        </>
    );
};

export default KonvaShapeItem;
